import type { Card, Copy, Cta, LeadItem, LinkCard, PageMeta, QaRow } from './types';

/**
 * Whether Demo Studio is live for reps. One flag: 'live' swaps the note under the Demo Studio cards and the last
 * sentence of the hero's supporting line.
 */
export type DemoStudioStatus = 'soon' | 'live';
export const demoStudioStatus: DemoStudioStatus = 'live';

/** Where "Sign in for early access" goes: the existing rep sign-in (one-time code, then the prospect's domain). */
export const REP_SIGN_IN = '/reps/sign-in';

const heroSupportingBase = "Their Salesforce, NetSuite or custom system is simulated, so you don't need their access.";

const studioCopy = {
  soon: {
    heroLast: 'Reps who bring Denamico into the deal get early access.',
    note: 'Demo Studio is launching soon. Reps who add Denamico to a deal get early access.',
  },
  live: {
    heroLast: 'Early access for reps who bring Denamico into the deal.',
    note: 'Demo Studio is in early access for reps who add Denamico to a deal.',
  },
} as const;

export function heroSupporting(status: DemoStudioStatus = demoStudioStatus): string {
  return `${heroSupportingBase} ${studioCopy[status].heroLast}`;
}

export function demoStudioNote(status: DemoStudioStatus = demoStudioStatus): string {
  return studioCopy[status].note;
}

/** The rep page at /reps. Verbatim copy; section order is layout order. Visual data is fictional. */
export const reps = {
  meta: {
    path: '/reps',
    name: 'Pairing for HubSpot reps',
    title: 'Pairing for HubSpot reps | Pairing by Denamico',
    description:
      "Come prepared for the integration question with a working demo built around your prospect's business, and an honest fit check you can run on the call.",
    ogImage: '/og/reps.png',
  } satisfies PageMeta,

  hero: {
    eyebrow: { text: 'For HubSpot account executives and growth specialists', pending: 'decision 6' } satisfies Copy,
    headline: 'Come prepared for the integration question with a working demo',
    subhead:
      "Demo Studio builds a live HubSpot integration demo around your prospect's business: their company, their industry and the values you heard on the call. Then you show them what happens when something breaks, and that no record gets dropped.",
    /** The smaller line under the subhead. Its last sentence follows demoStudioStatus (heroSupporting). */
    supporting: { text: heroSupporting(), pending: 'decision 7' } satisfies Copy,
    primary: { label: 'Try the fit check now', href: '/fit-check' } satisfies Cta,
    secondary: { label: 'How to get early access', href: '#access' } satisfies Cta,
    /**
     * Demo Studio's chat mid-design, as if the rep had just described the deal. The chips and the "Your demo" panel use
     * the real wording (packages/core/src/demo/chips.ts, features/demos/DesignPreview.tsx) for the Manufacturing
     * defaults on Salesforce. The prospect is fictional.
     */
    visual: {
      label: 'Illustration of the Demo Studio chat',
      title: 'Design a demo',
      prospect: 'for Northfield Packaging',
      said: 'Northfield Packaging runs Salesforce. Accounts and their installed equipment, synced with HubSpot both ways, every hour.',
      answer: 'I started from the manufacturing defaults: accounts sync with HubSpot companies, and equipment becomes a custom object linked to each one.',
      question: 'The demo uses custom objects. May Pairing create them, and new properties, in the HubSpot portal?',
      chips: ['Yes, create objects and properties', 'Properties only', 'Only use what the portal has'],
      preview: {
        title: 'Your demo',
        system: 'Salesforce',
        arrow: '↔',
        frequency: 'hourly',
        objects: [
          { label: 'Accounts', target: 'Companies' },
          { label: 'Equipment', target: 'Equipment (new custom object)' },
        ],
        creates: 'Pairing creates in HubSpot: the Equipment custom object and 6 properties',
      },
    },
  },

  stall: {
    headline: 'The integration question usually shows up late.',
    body: "You've got a champion and a timeline. Then someone asks how HubSpot will stay in sync with their ERP, or IT joins the third call with a list of field-mapping questions, or the prospect brings up the last integration that went wrong. Pairing is built for those moments:",
    list: [
      'an ERP, industry system or homegrown tool with no marketplace app',
      'a prospect who was burned by an integration before',
      "a technical buyer who needs specifics before they'll sign off",
      "a demo portal that looks nothing like the prospect's business",
    ],
  },

  whatYouGet: {
    headline: 'Two things to bring to that call.',
    body: 'When a deal stalls on "how will this stay in sync with our ERP?", you\'ll have something to show. Pairing gives you an honest answer you can get on the call, and a working demo you can put in front of the prospect.',
    cards: [
      {
        title: 'The fit check',
        body: 'Open to anyone, no sign-in. Describe what you heard and get a verdict, the honest limits and the questions to ask next.',
        link: { label: 'Try the fit check', href: '/fit-check' },
      },
      {
        title: 'Demo Studio',
        body: "Yours when you bring Denamico into the deal. A working integration into HubSpot, built around your prospect's business.",
        link: { label: 'How access works', href: '#access' },
      },
    ] satisfies LinkCard[],
  },

  demoStudio: {
    headline: 'Show them it works. Then show them it keeps working.',
    body: {
      text: "Describe the deal in your own words, or add the call transcript: the prospect's system, the records they keep in it, and what should land in HubSpot. Start from an industry's usual objects if you don't have a field list. Demo Studio simulates their system with made-up records shaped like their business, creates the HubSpot properties and custom objects the demo needs, and runs a real sync into your own HubSpot demo portal or a Denamico test portal. Then you pull the levers that show what happens when things go wrong.",
      pending: 'PDM question 6',
    } satisfies Copy,
    cards: [
      {
        title: 'It looks like their business.',
        body: 'Their company name, their industry and the dropdown values they use, syncing into HubSpot. The prospect sees something close to their world, not a sample company.',
      },
      {
        title: 'Nothing is dropped.',
        body: "A record that can't sync is held with a plain-language reason and a suggested fix. Fix it once, and every record held for that reason is replayed together.",
      },
      {
        title: 'Their system changes. Pairing notices.',
        body: 'A removed field or dropdown option, or a changed field type, raises an alert by email or as a HubSpot task. A new option is flagged with a suggested mapping. Their team hears about it in HubSpot, not weeks later from marketing.',
      },
      { title: 'The big first load.', body: "A large initial import runs through HubSpot's imports API." },
    ] satisfies Card[],
  },

  technical: {
    headline: 'Answers for when IT joins the call.',
    body: 'The technical buyer wants to know what happens after go-live. All of this comes with Pairing, so you can answer with specifics.',
    rows: [
      {
        question: 'Why does this record look like this?',
        answer: 'The record trace shows its whole history: what came in, how it was transformed and matched, and what was written, or why it was held.',
      },
      {
        question: 'Can we see sync errors inside HubSpot?',
        answer: 'Pairing can write sync status and the last error onto the HubSpot record, so failed records can go into a list or a report.',
      },
      {
        question: 'What if a mapping change goes wrong?',
        answer: 'Every mapping change is saved as a version and can be rolled back. A dry run shows what a change would create, update or hold before it touches either system.',
      },
      {
        question: 'Can we clean up our data first?',
        answer: 'A read-only scan finds likely duplicates, inconsistent dropdown values and badly formatted fields. Each fix can be applied once, with undo, or kept going forward.',
      },
      {
        question: 'We already have an integration.',
        answer: 'Pairing can run beside it in shadow mode, writing nothing, and report where its results match before anyone switches.',
      },
      {
        question: 'Who approves changes?',
        answer: "Until the client's team is handed off, Denamico reviews mapping changes before they go live.",
      },
    ] satisfies QaRow[],
  },

  fitOnCall: {
    headline: 'Check the integration before you promise it.',
    body: 'Describe what the prospect told you, in your own words. The Pairing fit check returns one of five verdicts (fits, likely fits, needs review, partial, or not a fit), plus the capabilities that match, the honest limits, the questions to ask next, and what to do now. A system it doesn\'t know gets "needs review", never a guess.',
    waysIntro: 'Two ways in, neither of which needs a sign-in:',
    ways: [
      { lead: 'On the web', body: 'at pairing.denamico.com/fit-check.', link: { label: 'pairing.denamico.com/fit-check', href: '/fit-check' } },
      { lead: 'For AI tools that read the web,', body: 'at pairing.denamico.com/llms.txt.', link: { label: 'pairing.denamico.com/llms.txt', href: '/llms.txt' } },
    ] satisfies LeadItem[],
    example: {
      label: 'Example',
      messages: [
        {
          who: 'Rep',
          text: '"Manufacturing prospect. Salesforce for sales, wants marketing in HubSpot. Their Salesforce admin edits the Industry picklist a lot, and they\'ve had lists break before."',
        },
        {
          who: 'Fit check',
          text: 'Likely fits. Drift detection catches picklist changes daily; value maps translate Industry values; records with unmatched values are held, held records alert in HubSpot, and they are replayed after a fix. Limit: if they only need standard Contact and Account sync with no error handling, HubSpot\'s native Salesforce integration may be enough. Ask next: Which Salesforce objects need to sync? Roughly how many records? Is any integration running today?',
        },
      ],
    },
  },

  access: {
    id: 'access',
    headline: "Bring us in, and we'll help you win it.",
    body: "Demo access is tied to a real deal where Denamico is the partner. That's how we stay accountable for what your prospect sees.",
    steps: [
      "Add Denamico to the deal as a partner, using HubSpot's standard Partner Assisted motion.",
      "Sign in to Pairing with your @hubspot.com email. You'll get a one-time code; there's no password.",
      "Enter the prospect's company domain.",
      "Pairing matches it to the shared deal automatically. If it can't, someone at Denamico approves it by hand. We don't turn requests away.",
      'Your access lasts 60 days, or until the deal closes.',
    ],
    button: { label: 'Sign in for early access', href: REP_SIGN_IN } satisfies Cta,
  },

  elsewhere: {
    headline: "We'll tell you when the native app is enough.",
    body: "If HubSpot's native Salesforce or NetSuite integration covers what the prospect needs, the fit check says so, and so will we. Pairing also isn't the right tool when:",
    list: [
      'the other system has no API and no database or files to read (EDI, a mainframe); SQL Server and files on an SFTP or FTPS server are in pilot, and other databases need review',
      'the process spans three or more systems or needs people to approve steps',
      'the volume is warehouse-scale',
    ],
    closing: 'A partner who sends you to the free option when it fits is one you can bring into the next deal.',
  },

  behind: {
    headline: 'Built by a Top 1% HubSpot Partner',
    body: 'Denamico is a Top 1% HubSpot Partner and one of the first partners in the world to earn all five HubSpot CRM accreditations, including Custom Integration. We built Pairing because every integration we delivered had to solve the same problems again: mapping, matching, retries and, most of all, showing the client what went wrong.',
  },

  final: {
    headline: 'Got a deal with an integration question?',
    primary: { label: 'Try the fit check now', href: '/fit-check' } satisfies Cta,
    secondary: { label: 'Sign in for early access', href: REP_SIGN_IN } satisfies Cta,
    smallPrint: 'Questions about partnering with Denamico on a deal? Email',
    partnerTodo: 'ERIN: partner team address',
  },
} as const;
